import { ActivatedRoute, Params, Router } from '@angular/router';
import { AfterViewChecked, Component, OnDestroy, OnInit } from '@angular/core';
import { HasPageTitleAndMetaData, StaticPageInterface } from '@app/interfaces/_index';
import { Meta, Title } from '@angular/platform-browser';

import { LearningPathsService } from '@app/services/_index';
import { StaticPage } from '@app/models/_index';
import { StaticPageAdapter } from '@app/adapters/_index';
import { Subscription } from 'rxjs';
import { scrollIntoView } from '@app/utilities/_index';

@Component({
  selector: 'app-learning-paths',
  templateUrl: './learning-paths.component.html',
  styleUrls: ['./learning-paths.component.scss'],
  standalone: false,
})
export class LearningPathsComponent
  implements OnInit, OnDestroy, HasPageTitleAndMetaData, AfterViewChecked
{
  // Page Properties
  learningPathContent: StaticPage;
  parentNavItems: StaticPage[] = []; // Parent nav items
  childNavItems: StaticPage[] = []; // Child nav items
  subs: Subscription[] = [];
  isLoading: boolean;
  fragment: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private learningPathsService: LearningPathsService,
    private titleService: Title,
    private metaService: Meta,
    private staticPageAdapter: StaticPageAdapter
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    // Calling the Title and Meta tag.
    this.setTitleAndMetaData();
    this.subs.push(
      this.route.params.subscribe((params: Params) => {
        // Pull Learning Path Content based on Slug selected.
        this.getLearningPathContentBySlug(params);
      })
    );
    // Call the Learning Path Content.
    this.getLearningPathContent();
  }

  /**
   * Routes the page based on the parameters passed
   */
  getLearningPathContent(): void {
    // Pull all the Learning Path content.
    this.getAllLearningPaths();
  }

  /**
   * Pull all the Learning Paths contents
   */
  getAllLearningPaths(): void {
    this.subs.push(
      this.learningPathsService.getLearningPathContent().subscribe({
        next: (response: StaticPageInterface[]) => {
          if (response && response.length > 0) {
            // TODO: This is repetitive in other pages, need to refactor
            const staticPages: StaticPage[] = response.map((item: StaticPageInterface) => {
              return this.staticPageAdapter.adapt(item);
            });
            // Filter out the parent quick start guide items that have no children if parent id is 0
            this.parentNavItems = staticPages.filter((staticPage) => staticPage?.parent === 0);
            this.childNavItems = staticPages.filter((staticPage) => staticPage?.parent !== 0);

            this.isLoading = false;
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log('Learning Path content cannot be loaded due to errors', error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Learning Path content completed');
        },
      })
    );
  }

  /**
   * Get Learning Path Content by slug
   * @param params indicates value for each slug selected
   */
  getLearningPathContentBySlug(params: Params): void {
    this.subs.push(
      this.learningPathsService.getLearningPathContent().subscribe({
        next: (response: StaticPageInterface[]) => {
          if (response && response.length > 0) {
            // Redirect to 404 page if the slug does not matches.
            if (!response.some((x) => x.slug === params.slug)) {
              this.router.navigateByUrl('pagenotfound');
            }
            response.filter((item: StaticPageInterface) => {
              if (item.slug === params.slug) {
                this.learningPathContent = this.staticPageAdapter.adapt(item);
              }
            });
            this.isLoading = false;
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log('Learning Path errors', error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Learning Paths content completed');
        },
      })
    );
    // The fragment allows the link # page jump to jump to specific content on page
    this.subs.push(
      this.route.fragment.subscribe((fragment: string) => {
        this.fragment = fragment;
      })
    );
  }

  /**
   * Set selected Learning Path title and metadata tags
   */
  setTitleAndMetaData(): void {
    // NOTE: Update page title on learning-path page
    this.titleService.setTitle('Learning Paths | Genesys Beyond');
    // NOTE: Update meta tag description for page
    this.metaService.updateTag({
      name: 'description',
      content:
        'See all of your Genesys Beyond enrollments, certifications, and recommended learning in one place.',
    });
  }

  /**
   * On Destroy unsubscribe the services to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  /**
   * Scroll to View using fragment or link a href # alias
   */
  ngAfterViewChecked(): void {
    if (this.fragment) {
      scrollIntoView(this.fragment, 'start');
    }
  }
}
